import EzController from 'controllers/ez-base-controller';
import Rails from 'rails-ujs';
import { ezClearFormErrors, ezErrorDisplay } from 'ez-styles/assets/js/utils/errors';
import { ezInitPasswordFields } from 'ez-styles/assets/js/forms/password_field';
import { ezInitAnimatedLabels } from 'ez-styles/assets/js/forms/animated_label';
import { parseErrorMsg, emailValidation } from 'lib/validation-helpers';
import { ezLoadingBtnOn, ezLoadingBtnOff } from 'ez-styles/assets/js/utils/buttons';
import { shouldBypassCaptcha } from 'lib/helpers';
import { findCategoryByElement, sendTrackingEvent } from 'components/analytics/conversions/tracking';
import { FORM_COMPLETE, FORM_ERROR } from 'components/analytics/conversions/constants/actions';

import 'detect-autofill';

export const FORM_AUTOFILLED_CLASS = 'is-autofilled';

export default class FormController extends EzController {
  static targets = [
    'formSubmitBtn',
  ];

  connect() {
    this.formValid = false;

    this.detectAutofill();

    if (this.animLabelInputs.length) {
      ezInitAnimatedLabels(this.animLabelInputs);
    }

    // If we have password fields, we need to make sure the js from the
    // style guide gets loaded
    if (this.element.querySelectorAll('.ez-input__field--password').length) {
      ezInitPasswordFields();
    }

    this.flagLoaded();
  }

  get animLabelInputs() {
    return [...this.element.querySelectorAll('.ez-input.ez-input--animated-label')];
  }

  detectAutofill() {
    document.addEventListener('onautocomplete', (event) => {
      // We need to disable autofill detection for checkboxes, radio buttons
      // and select dropdowns to avoid false positives.
      if (event.target.type === 'checkbox') return;
      if (event.target.type === 'radio') return;
      if (event.target.type === 'select') return;
      if (event.target.type === 'select-one') return;

      this.element.classList.add(FORM_AUTOFILLED_CLASS);
    });
  }

  submit() {
    if (shouldBypassCaptcha()) this.bypassRecaptcha();

    const formElement = this.element instanceof HTMLFormElement ? this.element : this.element.querySelector('form');

    if (formElement.getAttribute('data-remote') === 'true') {
      Rails.fire(formElement, 'submit');
    } else {
      formElement.submit();
    }
  }

  trackFormComplete({
    element = null,
    category = null,
    label = null,
  } = {}) {
    const isAutofilled = this.element.classList.contains(FORM_AUTOFILLED_CLASS);

    sendTrackingEvent({
      element: element || this.element,
      data: {
        action: FORM_COMPLETE,
        category: category || findCategoryByElement(this.element),
        label: label || `Autofill-${isAutofilled}`,
      },
    });
  }

  bypassRecaptcha() {
    const bypassRecaptchaHiddenField = document.querySelector('input#bypass_captcha');
    if (bypassRecaptchaHiddenField) bypassRecaptchaHiddenField.value = true;
  }

  triggerCaptchaAndValidateFields = (event) => {
    event.preventDefault();
    this.showSpinner();

    if (shouldBypassCaptcha()) {
      this.validateFields();
    } else {
      window.handleRecaptchaResponse = this.handleRecaptchaResponse;

      const { recaptchaId } = this.element.querySelector('.recaptcha-holder').dataset;

      window.grecaptcha.reset(recaptchaId);
      window.grecaptcha.execute(recaptchaId);
    }
  }

  handleRecaptchaResponse = () => {
    this.validateFields();
    window.handleRecaptchaResponse = null;
  }

  validateFields(selector = '[required="required"]') {
    if (!shouldBypassCaptcha()) this.resetFormErrors();

    this.checkFields(selector);

    if (!this.formValid) {
      this.hideSpinner();
    }
  }

  resetFormErrors() {
    ezClearFormErrors(this.element);
  }

  checkFormValidity(otherValidations = null) {
    this.formValid = true;

    const requiredFields = [
      ...this.element.querySelectorAll('input[required="required"]'),
      ...this.element.querySelectorAll('select[required="required"]'),
    ];

    requiredFields.forEach((input) => {
      const fieldWrap = input.closest('.ez-input');
      const ezInputField = fieldWrap.querySelector('.ez-input__field');

      let inputField = fieldWrap.contains(ezInputField) ? ezInputField : fieldWrap.querySelector('input');

      if (fieldWrap.classList.contains('ez-input--checkbox')) {
        inputField = fieldWrap.querySelector('[type="checkbox"]');

        if (!inputField.checked) {
          this.formValid = false;
        }
      }

      if (this.fieldHasError(inputField)) {
        this.formValid = false;
      }
    });

    if (otherValidations && otherValidations instanceof Function) {
      otherValidations();
    }

    if (this.formValid === true) {
      this.formSubmitBtnTarget.classList.remove('is-disabled');
    } else {
      this.formSubmitBtnTarget.classList.add('is-disabled');
    }
  }

  /**
  *  Form Error Handling helpers
  */
  fieldHasError(field) {
    if (((field.tagName === 'INPUT' || field.tagName === 'SELECT') && field.value === '')
        || (field.getAttribute('type') === 'email' && !emailValidation(field.value))
        || (field.classList.contains('error'))
        || (field.tagName === 'DIV' && !field.classList.contains('braintree-hosted-fields-valid'))
        || (field.closest('.ez-input.has-errors'))) {
      return true;
    }

    return false;
  }

  checkFields(selector) {
    this.formValid = true;

    const formFields = [
      ...this.element.querySelectorAll(selector),
    ];

    formFields.forEach((field) => {
      // Trim spaces from text input value
      if (field.getAttribute('type') === 'text') field.value = field.value.trim();

      this.checkForEmptyField(field);
      this.checkForSpecialChars(field);
      this.checkEmailFields(field);
    });

    // Validate any new password/password confirmation inputs
    // TODO: ES-1571 referencing password and password confirmation fields should be more generic
    const passwordField = this.element.querySelector('#user_password');
    const passwordConfirmField = this.element.querySelector('#user_password_confirmation');

    if (passwordField && passwordConfirmField) {
      this.checkPasswordFields({ passwordField, passwordConfirmField });
    } else if (passwordField) {
      this.checkPasswordField(passwordField);
    }
  }

  checkForEmptyField(field) {
    const thisFieldId = field.getAttribute('id');
    let fieldErrorMsg = '';

    if (parseErrorMsg(field, 'field_empty_error')) {
      fieldErrorMsg = parseErrorMsg(field, 'field_empty_error');
    }

    // Check for the special "accept terms" checkbox
    if (thisFieldId === 'user_accept_terms') {
      fieldErrorMsg = parseErrorMsg(field, 'checkbox_error');
    }

    this.displayErrorIfEmpty(thisFieldId, fieldErrorMsg);
  }

  checkForSpecialChars(field) {
    if (parseErrorMsg(field, 'special_characters')) {
      this.displayErrorIfSpecialChars(field);
    }
  }

  checkEmailFields(field) {
    // Make sure any email addresses entered are valid
    if (field.getAttribute('type') === 'email') {
      this.displayErrorIfEmailInvalid(field);
    }
  }

  checkPasswordField(passwordField) {
    if (passwordField && passwordField.checkValidity()) {
      // check the lengths
      if (this.displayErrorIfPasswordTooShort(passwordField)) {
        ezErrorDisplay(passwordField);
        ezErrorDisplay(
          passwordField,
          parseErrorMsg(passwordField, 'password_length'),
        );
        this.sendFormSubmissionErrorEvent(passwordField);
        this.formValid = false;
      }
    }
  }

  checkPasswordFields({ passwordField, passwordConfirmField }) {
    // First, check to make sure the password inputs are there and have values
    if (
      (passwordField && passwordField.checkValidity())
      || (passwordConfirmField && passwordConfirmField.checkValidity())
    ) {
      // If the passwords mismatch, then go ahead and show that error, if not check the lengths
      if (!this.displayErrorIfFieldMismatch(passwordField, passwordConfirmField, 'password_mismatch')
          && this.displayErrorIfPasswordTooShort(passwordField)) {
        ezErrorDisplay(passwordField);
        ezErrorDisplay(
          passwordConfirmField,
          parseErrorMsg(passwordConfirmField, 'password_length'),
        );
        this.sendFormSubmissionErrorEvent(passwordField);
        this.formValid = false;
      }
    }
  }

  displayFormError({ field, errorMessage, triggerType }) {
    if (this.formSubmitBtnTarget.classList.contains('has-spinner')) {
      this.hideSpinner();
    }

    const fieldWrapper = field.closest('.ez-input');

    if (!this.fieldShowingErrors(fieldWrapper)) {
      sendTrackingEvent({
        element: field,
        data: {
          action: FORM_ERROR,
          label: `Field-${field.dataset.englishLabel}`,
          category: 'Account',
          error_trigger: `Error-${triggerType}`,
        },
      });
    }

    ezErrorDisplay(field, errorMessage);

    this.formValid = false;
  }

  displayErrorIfEmpty(fieldId, fieldErrorMsg = '') {
    const thisField = this.element.querySelector(`#${fieldId}`);

    if (thisField !== null && !thisField.checkValidity()) {
      ezErrorDisplay(thisField, fieldErrorMsg);
      this.sendFormSubmissionErrorEvent(thisField);
      this.formValid = false;
    }
  }

  displayErrorIfPasswordTooShort(passwordField) {
    const { length } = passwordField.value;
    return length >= 0 && length < 6;
  }

  displayErrorIfFieldMismatch(field, confirmField, mismatchErrorKey) {
    if (field.value !== confirmField.value) {
      ezErrorDisplay(field);
      ezErrorDisplay(confirmField,
        parseErrorMsg(confirmField, mismatchErrorKey));

      this.sendFormSubmissionErrorEvent(field);
      this.formValid = false;
      return true;
    }

    return false;
  }

  displayErrorIfEmailInvalid(emailField) {
    if (
      (emailField
      && emailField.value
      && !emailValidation(emailField.value)) || !emailField.value
    ) {
      ezErrorDisplay(emailField, parseErrorMsg(emailField, 'invalid_email'));
      this.sendFormSubmissionErrorEvent(emailField);
      this.formValid = false;
    }
  }

  displayErrorIfSpecialChars(field) {
    if (this.hasInvalidChars(field.value)) {
      ezErrorDisplay(field, parseErrorMsg(field, 'special_characters'));
      this.sendFormSubmissionErrorEvent(field);
      this.formValid = false;
    }
  }

  fieldShowingErrors(field) {
    return field.classList.contains('has-errors');
  }

  hasInvalidChars(term) {
    return term.match(/[^a-zA-Z0-9 ]/g);
  }

  showSpinner() {
    if (this.hasFormSubmitBtnTarget && !this.formSubmitBtnTarget.classList.contains('has-spinner')) {
      this.disableWithAttr = this.formSubmitBtnTarget.getAttribute('data-disable-with');

      this.formSubmitBtnTarget.removeAttribute('data-disable-with');

      ezLoadingBtnOn(this.formSubmitBtnTarget);
    }
  }

  hideSpinner() {
    if (this.hasFormSubmitBtnTarget && this.formSubmitBtnTarget.classList.contains('has-spinner')) {
      ezLoadingBtnOff(this.formSubmitBtnTarget);

      this.formSubmitBtnTarget.setAttribute('data-disable-with', this.disableWithAttr);
    }
  }

  sendFormSubmissionErrorEvent(field) {
    sendTrackingEvent({
      element: field,
      data: {
        action: FORM_ERROR,
        label: `Field-${field.dataset.englishLabel}`,
        category: 'Account',
        error_trigger: 'Error-Submission',
      },
    });
  }
}
